import React from 'react';
import { Chip, Stack, Tooltip, Typography, Link } from '@mui/material';
import { Subscription, UserMembershipView, UserRole } from '@localstack/types';
import { capitalise, formatDateTo, pluralizeOrDash } from '@localstack/services';
import { UserAvatar, UserIdentity } from '@localstack/ui';

import { LicenseAssignmentDropdown } from './LicenseAssignmentDropdown';
import { MembershipActionsDropdown } from './MembershipActionsDropdown';
import { resolveMembershipActions } from './membershipActions';
import { ScimStatus, SeatAvailability } from './types';

export interface MemberRowProps {
  member: UserMembershipView;
  assignedSubscriptions: string[];
  allSubscriptions: Record<string, Subscription>;
  availableSubscriptions: Record<string, Subscription>;
  availableSubscriptionSeats: SeatAvailability;
  canUpdateMembers: boolean;
  canDeleteMembers: boolean;
  isLastAdmin: boolean;
  scimInfo: ScimStatus;
  onChangeAssignments: (subscriptionIds: string[]) => Promise<void>;
  onUpdateRole: (userId: string, role: UserRole) => Promise<void>;
  onRequestEditPermissions: () => void;
  onRemoveMember: (userId: string) => Promise<void>;
  onUserClick?: (userId: string) => void;
}

export const MemberRow = ({
  member,
  assignedSubscriptions,
  allSubscriptions,
  availableSubscriptions,
  availableSubscriptionSeats,
  canUpdateMembers,
  canDeleteMembers,
  isLastAdmin,
  scimInfo,
  onChangeAssignments,
  onUpdateRole,
  onRequestEditPermissions,
  onRemoveMember,
  onUserClick,
}: MemberRowProps): JSX.Element => {
  const scimManaged = scimInfo.active && !!member.is_scim_provisioned;
  const actions = resolveMembershipActions(member, { canUpdateMembers, canDeleteMembers, isLastAdmin, scimManaged });
  const permissionsLabel = `+${pluralizeOrDash(member.membership_details.permissions.length, 'permission', 'permissions')}`;

  return (
    <React.Fragment>
      <UserAvatar email={member.user_email} />
      <UserIdentity
        fullName={member.user_full_name}
        email={member.user_email}
        onNameClick={onUserClick ? () => onUserClick(member.user_id) : undefined}
        badge={
          scimManaged && (
            <Chip
              label="SCIM"
              size="small"
              variant="outlined"
              sx={{
                height: (theme) => theme.spacing(2.5),
                flexShrink: 0,
                '& .MuiChip-label': { fontSize: (theme) => theme.typography.caption.fontSize },
              }}
            />
          )
        }
      />
      <div />
      <LicenseAssignmentDropdown
        allSubscriptions={allSubscriptions}
        availableSubscriptions={availableSubscriptions}
        availableSubscriptionSeats={availableSubscriptionSeats}
        assignedSubscriptions={assignedSubscriptions}
        onChangeAssignments={onChangeAssignments}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <div>
          <Typography variant="caption">{capitalise(member.membership_details.role)}</Typography>
        </div>
        {member.membership_details.role !== UserRole.ADMIN &&
          member.membership_details.permissions.length > 0 &&
          (actions.edit_permissions.allowed ? (
            <Link underline="always" variant="caption" color="textSecondary" onClick={onRequestEditPermissions}>
              {permissionsLabel}
            </Link>
          ) : (
            <Tooltip title={actions.edit_permissions.reason ?? ''}>
              <Typography variant="caption" color="textSecondary">
                {permissionsLabel}
              </Typography>
            </Tooltip>
          ))}
      </Stack>
      <div>
        <Typography variant="caption">{formatDateTo(member.created, 'MMM D, YYYY')}</Typography>
      </div>
      <MembershipActionsDropdown
        member={member}
        actions={actions}
        onUpdateRole={onUpdateRole}
        onRequestEditPermissions={onRequestEditPermissions}
        onRemoveMember={onRemoveMember}
      />
    </React.Fragment>
  );
};
